import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useFeatures } from '@/lib/useFeatures';
import { notifyXp } from '@/lib/xpToast';
import { Flame, Trash2, Sparkles, RefreshCw, ChevronRight, ChevronLeft, ArrowRight, Lock } from 'lucide-react';

/**
 * THE FORGE
 *
 * A three-stage ritual:
 *  1. NAME IT       — user types the limiting belief (appears as carved iron)
 *  2. BURN IT       — tap & hold to ignite. Ember canvas fires while heat increases.
 *  3. REBUILD IT    — user types the gold replacement belief, saved to private Forge Log.
 *
 * Persistence:
 *  - Entries saved via /forge endpoints (user-scoped, never public).
 *  - List displayed below with delete control.
 */

const STEPS = ['name', 'burn', 'rebuild'];
const STEP_LABEL = { name: 'Name it', burn: 'Burn it', rebuild: 'Rebuild it' };

// Canvas ember particle system. Lightweight enough for mobile.
function useEmberCanvas(canvasRef, { intensity }) {
  const animRef = useRef(null);
  const particlesRef = useRef([]);
  const intensityRef = useRef(intensity);
  useEffect(() => { intensityRef.current = intensity; }, [intensity]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const resize = () => {
      const r = canvas.getBoundingClientRect();
      canvas.width = r.width * dpr;
      canvas.height = r.height * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    const onResize = () => { ctx.setTransform(1, 0, 0, 1, 0, 0); resize(); };
    window.addEventListener('resize', onResize);

    const spawn = () => {
      const r = canvas.getBoundingClientRect();
      const cx = r.width / 2;
      const cy = r.height * 0.92;
      const i = intensityRef.current;
      const spreadX = 60 + i * 200;
      const baseV = 1 + i * 4;
      particlesRef.current.push({
        x: cx + (Math.random() - 0.5) * spreadX,
        y: cy + (Math.random() - 0.5) * 8,
        vx: (Math.random() - 0.5) * 0.6,
        vy: -baseV - Math.random() * baseV,
        life: 0,
        max: 70 + Math.random() * 60,
        size: 1.4 + Math.random() * 2.6,
        hue: 22 + Math.random() * 20,
      });
    };

    const step = () => {
      const r = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, r.width, r.height);
      // Spawn new embers proportional to intensity.
      const i = intensityRef.current;
      const spawnCount = Math.floor(0.4 + i * 9);
      for (let s = 0; s < spawnCount; s++) spawn();

      const next = [];
      for (const p of particlesRef.current) {
        p.life += 1;
        if (p.life >= p.max) continue;
        p.x += p.vx;
        p.y += p.vy;
        p.vy *= 0.99;
        p.vx += (Math.random() - 0.5) * 0.1;
        const t = p.life / p.max;
        const alpha = (1 - t) * 0.95;
        const sat = 100;
        const light = 55 - t * 25;
        ctx.beginPath();
        ctx.fillStyle = `hsla(${p.hue}, ${sat}%, ${light}%, ${alpha})`;
        ctx.shadowColor = `hsla(${p.hue}, 100%, 60%, ${alpha})`;
        ctx.shadowBlur = 8 + i * 12;
        ctx.arc(p.x, p.y, p.size * (1 - t * 0.4), 0, Math.PI * 2);
        ctx.fill();
        next.push(p);
      }
      ctx.shadowBlur = 0;
      particlesRef.current = next;
      animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [canvasRef]);
}

export default function Forge() {
  const { user } = useAuth();
  const { isOn, loading: flagsLoading } = useFeatures();
  const nav = useNavigate();
  const [step, setStep] = useState('name');
  const [limiting, setLimiting] = useState('');
  const [newBelief, setNewBelief] = useState('');
  const [intensity, setIntensity] = useState(0);
  const [burning, setBurning] = useState(false);
  const [shattered, setShattered] = useState(false);
  const [savedEntry, setSavedEntry] = useState(null);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const holdStartRef = useRef(0);
  const heatRafRef = useRef(null);

  useEmberCanvas(canvasRef, { intensity: burning ? intensity : 0 });

  // Load entries.
  const loadEntries = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/forge');
      setEntries(data.entries || []);
    } catch (e) {
      // Silent fail; feature may be disabled.
    }
  }, [user]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  if (flagsLoading) return <div className="kc-container py-16 text-muted-kc" data-testid="forge-loading">Stoking the fire…</div>;
  if (!isOn('forge')) {
    return (
      <div className="kc-container py-16" data-testid="forge-disabled">
        <div className="text-[11px] tracking-[0.18em] uppercase text-gold">The Forge</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Cooling down.</h1>
        <p className="mt-3 text-muted-kc max-w-prose">The Forge is not lit right now. Check back soon.</p>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="kc-container py-20 text-center" data-testid="forge-locked">
        <Lock size={32} className="mx-auto text-gold"/>
        <h1 className="font-display uppercase text-4xl md:text-5xl mt-3">Sign in to enter the Forge.</h1>
        <p className="mt-3 text-muted-kc">The Forge is private. Your entries live only with you.</p>
        <div className="mt-6 flex gap-3 justify-center">
          <Link to="/login?next=/forge" className="h-11 px-5 inline-flex items-center rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="forge-login">Log in</Link>
          <Link to="/signup" className="h-11 px-5 inline-flex items-center rounded-[var(--kc-radius-sm)] border border-kc-strong font-semibold hover:bg-surface-2 transition-colors" data-testid="forge-signup">Create account</Link>
        </div>
      </div>
    );
  }

  const beginHold = () => {
    if (!limiting.trim()) return;
    setBurning(true);
    holdStartRef.current = Date.now();
    const tick = () => {
      const elapsed = (Date.now() - holdStartRef.current) / 1000;
      // Reach max intensity (1.0) at ~3.5 seconds of hold.
      const next = Math.min(1, elapsed / 3.5);
      setIntensity(next);
      if (next >= 1) {
        setShattered(true);
        setBurning(false);
        setTimeout(() => setStep('rebuild'), 900);
        return;
      }
      heatRafRef.current = requestAnimationFrame(tick);
    };
    heatRafRef.current = requestAnimationFrame(tick);
  };
  const endHold = () => {
    setBurning(false);
    if (heatRafRef.current) cancelAnimationFrame(heatRafRef.current);
    if (!shattered) setIntensity(0);
  };

  const saveEntry = async () => {
    setError('');
    setSaving(true);
    try {
      const seconds = Math.round((Date.now() - (holdStartRef.current || Date.now())) / 1000);
      const { data } = await api.post('/forge', {
        limiting_belief: limiting,
        new_belief: newBelief,
        ritual_seconds: seconds,
      });
      setSavedEntry(data);
      if (data?.xp?.xp_gained) {
        notifyXp(data.xp, 'Forge complete · belief rewritten');
        window.dispatchEvent(new Event('kc:xp-updated'));
      }
      await loadEntries();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save your forge entry.');
    } finally {
      setSaving(false);
    }
  };

  const resetRitual = () => {
    setLimiting(''); setNewBelief(''); setIntensity(0); setBurning(false);
    setShattered(false); setSavedEntry(null); setStep('name'); setError('');
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this forge entry? This cannot be undone.')) return;
    try {
      await api.delete(`/forge/${id}`);
      setEntries((rows) => rows.filter((r) => r.id !== id));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not delete entry.');
    }
  };

  const stepIndex = STEPS.indexOf(step);

  return (
    <div className="bg-ink text-white min-h-[calc(100vh-5rem)]" data-testid="forge-page">
      {/* ===================== Ritual ===================== */}
      <div className="relative overflow-hidden">
        {/* Ember canvas behind everything */}
        <canvas ref={canvasRef} aria-hidden="true" className="absolute inset-0 w-full h-full pointer-events-none"/>
        {/* Background gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_110%,rgba(255,107,0,0.18),transparent_55%)] pointer-events-none" aria-hidden/>

        <div className="relative kc-container py-12 md:py-20">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-[11px] tracking-[0.18em] uppercase text-gold">The Forge</div>
            <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.95] mt-3">Burn the lie.<br/>Forge the truth.</h1>
            <p className="mt-5 text-white/65 text-base md:text-lg">Type the story that's been holding you down. Burn it. Replace it with what's actually true.</p>
          </div>

          {/* Step indicator */}
          <div className="mt-10 flex items-center justify-center gap-3" data-testid="forge-steps">
            {STEPS.map((s, i) => {
              const reached = i <= stepIndex;
              return (
                <React.Fragment key={s}>
                  <div className={`flex items-center gap-2 ${reached ? 'text-[color:var(--kc-gold)]' : 'text-white/35'}`}>
                    <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-display border ${reached ? 'border-[color:var(--kc-gold)] bg-[color:var(--kc-gold)]/10' : 'border-white/20'}`}>{i+1}</span>
                    <span className="text-[10px] tracking-[0.18em] uppercase font-semibold hidden sm:inline">{STEP_LABEL[s]}</span>
                  </div>
                  {i < STEPS.length - 1 && <span className={`w-8 h-px ${i < stepIndex ? 'bg-[color:var(--kc-gold)]' : 'bg-white/15'}`} aria-hidden/>}
                </React.Fragment>
              );
            })}
          </div>

          {/* Stage content */}
          <div className="mt-10 md:mt-14 max-w-2xl mx-auto">
            {step === 'name' && (
              <div data-testid="forge-stage-name">
                <label className="block text-[10px] tracking-[0.2em] uppercase text-white/50 mb-3 text-center">The lie you tell yourself</label>
                <textarea
                  value={limiting}
                  onChange={(e) => setLimiting(e.target.value)}
                  rows={3}
                  maxLength={280}
                  placeholder='e.g. "I&apos;m not enough."'
                  className="forge-iron-input w-full p-5 rounded-[var(--kc-radius-md)] bg-black/50 border border-white/15 focus:border-[color:var(--kc-gold)] outline-none text-white font-display uppercase text-2xl md:text-3xl text-center tracking-wide resize-none transition-colors"
                  data-testid="forge-input-limiting"
                />
                <div className="mt-2 text-right text-[10px] text-white/40 uppercase tracking-wider">{limiting.length}/280</div>
                <button
                  type="button"
                  disabled={!limiting.trim()}
                  onClick={() => setStep('burn')}
                  className="mt-6 w-full h-12 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                  data-testid="forge-name-next"
                >
                  To the fire <ArrowRight size={16}/>
                </button>
              </div>
            )}

            {step === 'burn' && (
              <div className="text-center" data-testid="forge-stage-burn">
                <p className="text-white/65 text-sm mb-6">Press and hold the belief. Don't let go until it shatters.</p>
                <div
                  className="relative select-none"
                  onMouseDown={beginHold}
                  onMouseUp={endHold}
                  onMouseLeave={endHold}
                  onTouchStart={(e) => { e.preventDefault(); beginHold(); }}
                  onTouchEnd={endHold}
                >
                  <div
                    className={`forge-iron-text font-display uppercase text-3xl md:text-5xl text-center leading-tight py-8 px-6 rounded-[var(--kc-radius-md)] cursor-pointer transition-all duration-200 ${shattered ? 'forge-shattered' : ''}`}
                    style={{
                      // Heat blend: from cold iron grey to molten gold/orange.
                      color: shattered
                        ? 'rgba(255,255,255,0)'
                        : `rgb(${Math.round(180 + intensity * 75)}, ${Math.round(180 - intensity * 40)}, ${Math.round(180 - intensity * 140)})`,
                      textShadow: intensity > 0.1 ? `0 0 ${Math.round(intensity * 40)}px rgba(255, ${Math.round(160 - intensity * 100)}, 0, ${0.4 + intensity * 0.5})` : 'none',
                      filter: shattered ? 'blur(3px)' : `brightness(${1 + intensity * 0.4})`,
                      transform: burning ? `scale(${1 + intensity * 0.04})` : 'scale(1)',
                      transition: shattered ? 'opacity 0.7s ease-out, filter 0.7s' : 'none',
                      opacity: shattered ? 0 : 1,
                      background: intensity > 0.15 ? `radial-gradient(ellipse at center, rgba(255, ${Math.round(120 - intensity * 80)}, 0, ${intensity * 0.18}), transparent 70%)` : 'transparent',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                    data-testid="forge-burn-text"
                  >
                    {limiting}
                  </div>
                  {/* Heat progress ring underneath */}
                  <div className="mt-4 h-1.5 rounded-full bg-white/10 overflow-hidden" aria-hidden>
                    <div className="h-full bg-gradient-to-r from-orange-500 via-[color:var(--kc-gold)] to-yellow-200 transition-[width] duration-100" style={{ width: `${intensity * 100}%` }}/>
                  </div>
                  <div className="mt-3 text-[10px] tracking-[0.2em] uppercase text-white/40">
                    {shattered ? 'It is gone.' : burning ? `Burning… ${Math.round(intensity * 100)}%` : 'Press & hold to ignite'}
                  </div>
                </div>
                {!shattered && (
                  <button type="button" onClick={() => setStep('name')} className="mt-8 text-xs uppercase tracking-wider text-white/45 hover:text-white inline-flex items-center gap-1" data-testid="forge-burn-back"><ChevronLeft size={14}/> Rewrite the lie</button>
                )}
              </div>
            )}

            {step === 'rebuild' && (
              <div data-testid="forge-stage-rebuild">
                {!savedEntry ? (
                  <>
                    <div className="text-center text-[10px] tracking-[0.2em] uppercase text-white/50 mb-3">In place of that lie…</div>
                    <div className="text-center mb-6">
                      <Sparkles size={20} className="text-[color:var(--kc-gold)] inline-block animate-pulse"/>
                    </div>
                    <label className="block text-[10px] tracking-[0.2em] uppercase text-white/50 mb-3 text-center">The truth you are choosing</label>
                    <textarea
                      value={newBelief}
                      onChange={(e) => setNewBelief(e.target.value)}
                      rows={3}
                      maxLength={280}
                      placeholder='e.g. "I am enough, exactly as I am right now."'
                      className="forge-gold-input w-full p-5 rounded-[var(--kc-radius-md)] bg-[color:var(--kc-gold)]/5 border border-[color:var(--kc-gold)]/40 focus:border-[color:var(--kc-gold)] outline-none text-[color:var(--kc-gold)] font-display uppercase text-2xl md:text-3xl text-center tracking-wide resize-none transition-colors"
                      data-testid="forge-input-new"
                    />
                    <div className="mt-2 text-right text-[10px] text-white/40 uppercase tracking-wider">{newBelief.length}/280</div>
                    {error && <div className="mt-3 bg-red-500/10 border border-red-500/40 text-red-200 rounded-[var(--kc-radius-sm)] p-3 text-sm" data-testid="forge-error">{error}</div>}
                    <div className="mt-6 flex gap-3">
                      <button
                        type="button"
                        onClick={resetRitual}
                        className="h-12 px-4 rounded-[var(--kc-radius-sm)] border border-white/20 text-white/70 font-semibold hover:bg-white/5 transition-colors"
                        data-testid="forge-rebuild-restart"
                      >Start over</button>
                      <button
                        type="button"
                        disabled={!newBelief.trim() || saving}
                        onClick={saveEntry}
                        className="flex-1 h-12 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors disabled:opacity-30 inline-flex items-center justify-center gap-2"
                        data-testid="forge-rebuild-save"
                      >
                        {saving ? <><RefreshCw size={16} className="animate-spin"/> Forging</> : <>Forge it <Flame size={16}/></>}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center forge-victory" data-testid="forge-success">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--kc-gold)]">Forged in gold</div>
                    <div className="mt-5 font-display uppercase text-3xl md:text-5xl leading-tight text-[color:var(--kc-gold)] forge-gold-shimmer">
                      {savedEntry.new_belief || newBelief}
                    </div>
                    <div className="mt-5 text-sm text-white/55">Saved to your private Forge Log.</div>
                    <button type="button" onClick={resetRitual} className="mt-8 h-11 px-5 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors inline-flex items-center gap-2" data-testid="forge-success-again">Forge another <ChevronRight size={14}/></button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===================== Forge Log ===================== */}
      <div className="border-t border-white/10 bg-black">
        <div className="kc-container py-12 md:py-16">
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-gold">Forge Log</div>
              <h2 className="font-display uppercase text-3xl md:text-4xl mt-2">{entries.length} {entries.length === 1 ? 'belief rewritten' : 'beliefs rewritten'}</h2>
            </div>
            {entries.length > 0 && <button type="button" onClick={() => nav('/forge')} className="text-xs uppercase tracking-wider text-white/50 hover:text-white">Private to you</button>}
          </div>

          {entries.length === 0 ? (
            <div className="mt-8 rounded-[var(--kc-radius-md)] border border-white/10 bg-white/[0.03] p-8 text-center" data-testid="forge-log-empty">
              <Flame size={28} className="text-[color:var(--kc-gold)] mx-auto"/>
              <div className="mt-3 text-white/65">No beliefs in the Forge yet. The first one is the hardest. After that, you'll be hooked.</div>
            </div>
          ) : (
            <ul className="mt-6 grid md:grid-cols-2 gap-3" data-testid="forge-log">
              {entries.map((row) => (
                <li key={row.id} className="group bg-white/[0.04] border border-white/10 hover:border-[color:var(--kc-gold)]/40 rounded-[var(--kc-radius-md)] p-5 transition-colors" data-testid={`forge-entry-${row.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">The lie</div>
                      <div className="text-white/60 line-through italic mt-1 text-sm break-words">{row.limiting_belief}</div>
                      <div className="mt-4 text-[10px] tracking-[0.2em] uppercase text-[color:var(--kc-gold)]">Forged truth</div>
                      <div className="font-display uppercase text-lg md:text-xl text-[color:var(--kc-gold)] mt-1 break-words">{row.new_belief || '—'}</div>
                      <div className="mt-3 text-[10px] tracking-[0.18em] uppercase text-white/35">{new Date(row.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    </div>
                    <button type="button" onClick={() => deleteEntry(row.id)} className="opacity-30 hover:opacity-100 hover:text-red-300 transition-all p-1" aria-label="Delete entry" data-testid={`forge-delete-${row.id}`}>
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Forge styling */}
      <style>{`
        .forge-iron-input { letter-spacing: 0.05em; }
        .forge-gold-input { letter-spacing: 0.05em; box-shadow: 0 0 24px rgba(212, 164, 55, 0.12) inset; }
        .forge-gold-shimmer {
          background: linear-gradient(110deg, #d4a437 20%, #f4d27d 45%, #ffe9a8 50%, #f4d27d 55%, #d4a437 80%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: forge-shimmer 4.5s linear infinite;
        }
        @keyframes forge-shimmer { to { background-position: 200% center; } }
        .forge-shattered { pointer-events: none; }
        .forge-victory { animation: forge-rise 0.7s ease-out both; }
        @keyframes forge-rise { from { transform: translateY(14px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
}
